/**
 * ShootPanel — universal capture control used by every tool.
 *
 * Renders one of two UIs depending on the global Prism Mode flag:
 *   • REFLECTORLESS  → classic manual "SHOOT" button (visible red laser)
 *   • PRISM          → Search + Lock + Auto-shoot on prism-still
 *
 * Every tool that used to render its own big "Take shot" button now
 * drops this in instead:
 *
 *     <ShootPanel onShot={(m) => addShotToSession(m)} />
 *
 * The onShot callback receives the full measurement object
 *     { hz, v, sd, x, y, z, timestamp }
 * exactly matching what `station.measureLocal()` returns.
 *
 * Prism-mode workflow the surveyor drives from the tablet:
 *   1. Point the scope roughly at the prism using the AimPad.
 *   2. Tap SEARCH → PowerSearch spins the head 360° until it locks.
 *   3. Head follows the prism live.
 *   4. When Hz/V stay within `settleRad` for `settleMs`, one shot
 *      fires automatically and the point is handed to the tool.
 *   5. Move the prism → re-arm → next stop → next shot. Forever.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Crosshair, ScanSearch, PauseCircle } from "lucide-react";
import { station } from "../lib/api";
import { usePrismMode } from "../lib/prism-mode";

// Settle-detector defaults — tuned for the TS16 P (its lock jitters
// ~1 arc-sec while holding on a prism).
// Settle-detector defaults — snappy auto-shoot for a stationary prism.
//   settleRad: how tight the Hz/V ring buffer has to be before we
//     declare "still". 0.3 mrad ≈ 0.6 mm at 2 m — comfortably inside
//     the TS16 P's ATR jitter while locked.
//   settleMs:  how long the prism must be still. 600 ms feels
//     instant to the surveyor but rejects the mid-move momentum.
//   rearmRad:  the MINIMUM angular distance (in radians) the prism
//     must travel from the last-fired shot before the auto-shot
//     re-arms. 2.5 mrad ≈ 5 mm at 2 m — jitter or a bump won't
//     re-fire; only a deliberate "move me to the next spot" will.
const DEFAULT_SETTLE_RAD = 0.0003;
const DEFAULT_SETTLE_MS  = 350;
const DEFAULT_REARM_RAD  = 0.0025;

const useLiveScopeLock = () => {
  const [s, setS] = useState({ hz: null, v: null, atr_locked: false });
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await station.state();
        if (!cancelled) setS({
          hz: r.hz, v: r.v, atr_locked: !!r.atr_locked,
        });
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 250);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return s;
};

export const ShootPanel = ({
  onShot,
  label = "Shoot",
  disabled = false,
  settleRad = DEFAULT_SETTLE_RAD,
  settleMs = DEFAULT_SETTLE_MS,
}) => {
  const [prismMode] = usePrismMode();
  return prismMode
    ? <PrismShootPanel onShot={onShot} disabled={disabled} settleRad={settleRad} settleMs={settleMs} />
    : <ManualShootButton onShot={onShot} disabled={disabled} label={label} />;
};

/* --- Reflectorless: one big button ---------------------------------- */
const ManualShootButton = ({ onShot, disabled, label }) => {
  const [busy, setBusy] = useState(false);
  const fire = async () => {
    setBusy(true);
    try {
      await station.laser(true);
      const m = await station.measureLocal();
      if (!Number.isFinite(m?.sd)) throw new Error("no distance returned");
      onShot?.(m);
      toast.success(`Shot · sd ${m.sd.toFixed(3)} m`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Measure failed");
    } finally { setBusy(false); }
  };
  return (
    <button data-testid="shootpanel-manual" disabled={busy || disabled} onClick={fire}
      className="min-h-[64px] w-full rounded-md border border-[#00E1C7] bg-gradient-to-br from-[#00E1C7]/20 to-[#005a52]/20 py-3 text-sm font-black uppercase tracking-widest text-[#00E1C7] disabled:opacity-40"
      style={{ fontFamily: "Chakra Petch, sans-serif" }}>
      <div className="flex items-center justify-center gap-2">
        <Crosshair size={20} strokeWidth={2.5} />
        {busy ? "Measuring…" : label}
      </div>
    </button>
  );
};

/* --- Prism mode: search + lock + auto-shoot ------------------------- */
const PrismShootPanel = ({ onShot, disabled, settleRad, settleMs }) => {
  const scope = useLiveScopeLock();
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("idle");   // idle | searching | armed | shooting | disarmed | lost
  const [autoShoot, setAutoShoot] = useState(true);
  // Auto-reacquire toggle. Default OFF — on FlexLine/Captivate firmware
  // MOT_ReadLockStatus can dip to zero even during a healthy lock and
  // an automatic re-search would break tracking every time. Leave it
  // to the surveyor: tap the Reacquire button (or the header pill) when
  // the lock is actually gone.
  const [autoReacquire, setAutoReacquire] = useState(false);

  const armStartRef = useRef(null);
  // Hz/V of the last fired shot — used to detect whether the prism
  // has moved to a genuinely new spot before we're allowed to arm
  // again. Jitter or a small bump on the pole won't clear this.
  const lastShotRef = useRef(null);
  const historyRef = useRef([]);
  // Track when the lock was first observed to be missing. Used to
  // decide whether it's a transient flicker (ignore) or a real
  // drop (trigger auto-reacquire).
  const lostSinceRef = useRef(null);
  const reacquiringRef = useRef(false);

  // Force target-type = prism on mount, restore reflectorless on unmount.
  useEffect(() => {
    station.setTargetType(0).catch(() => { /* silent */ });
    return () => {
      station.atr("unlock").catch(() => { /* silent */ });
      station.setTargetType(1).catch(() => { /* silent */ });
    };
  }, []);

  const fire = useCallback(async () => {
    if (mode === "shooting") return;
    setMode("shooting");
    armStartRef.current = null;
    try {
      const m = await station.measureLocal();
      if (!Number.isFinite(m?.sd)) throw new Error("no distance returned");
      lastShotRef.current = { hz: m.hz, v: m.v };
      onShot?.(m);
      toast.success(`Auto-shot · sd ${m.sd.toFixed(3)} m`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Measure failed");
    } finally {
      // Land in DISARMED — the prism has to move by at least
      // DEFAULT_REARM_RAD before the arm-detector will fire again.
      setMode("disarmed");
    }
  }, [mode, onShot]);

  // Fire an auto-reacquire cycle: slew back to the last known good
  // Hz/V (cached in the bridge) and re-run PowerSearch. Guarded so
  // multiple lost-lock ticks in a row don't fire it in parallel.
  const doReacquire = useCallback(async () => {
    if (reacquiringRef.current) return;
    reacquiringRef.current = true;
    setMode("searching");
    try {
      await station.reacquire();
      toast.success("Reacquired — move to your next point");
      // Same reasoning as fresh Search: don't fire immediately.
      // Reference for re-arm = wherever we ended up locked.
      lastShotRef.current = { hz: scope.hz, v: scope.v };
      setMode("disarmed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Reacquire failed");
      setMode("lost");
    } finally {
      lostSinceRef.current = null;
      reacquiringRef.current = false;
    }
  }, [scope.hz, scope.v]);

  // Watch the live scope; state machine decides when to fire.
  useEffect(() => {
    if (mode === "shooting" || mode === "idle" || mode === "searching") return;
    const hz = scope.hz, v = scope.v;
    if (!Number.isFinite(hz) || !Number.isFinite(v)) return;

    historyRef.current.push({ hz, v });
    if (historyRef.current.length > 8) historyRef.current.shift();

    if (!scope.atr_locked) {
      // Lock is missing. The bridge already filters out sub-second
      // ATR flickers with its own hysteresis, but on top of that we
      // give the surveyor 1.5 s of grace so a genuine prism-move
      // doesn't accidentally trigger a reacquire.
      if (lostSinceRef.current == null) lostSinceRef.current = Date.now();
      if (mode !== "disarmed" && mode !== "lost") setMode("lost");
      armStartRef.current = null;
      const lostFor = Date.now() - lostSinceRef.current;
      if (autoReacquire && lostFor > 1500 && !reacquiringRef.current) {
        doReacquire();
      }
      return;
    }

    // Lock recovered — clear the lost-timer.
    lostSinceRef.current = null;

    if (!autoShoot) return;

    // If we're DISARMED (after a shot or right after a fresh Search),
    // wait until the prism has been moved a meaningful distance from
    // the reference point (last shot Hz/V, or the point at which the
    // lock was acquired) before letting the arm-detector fire again.
    // This kills two bugs:
    //   * one shot fires, then another 0.5 s later because the prism
    //     wobbled 0.5 mm on the pole.
    //   * SEARCH succeeds and IMMEDIATELY fires a shot at the spot
    //     where the pole happens to be resting — even though the
    //     surveyor hasn't actually placed it on a survey point yet.
    if (mode === "disarmed") {
      const ref = lastShotRef.current;
      if (!ref) return;             // no reference yet, keep waiting
      const dHz = Math.abs(hz - ref.hz);
      const dV  = Math.abs(v  - ref.v );
      const moved = Math.hypot(dHz, dV) > DEFAULT_REARM_RAD;
      if (!moved) return;           // still parked at the reference
      // Meaningful move detected → let normal armed/still logic
      // resume by falling through with mode set to armed.
      setMode("armed");
    }

    // Compute Hz/V range over the recent buffer.
    const hz_vals = historyRef.current.map((h) => h.hz);
    const v_vals = historyRef.current.map((h) => h.v);
    const hz_range = Math.max(...hz_vals) - Math.min(...hz_vals);
    const v_range = Math.max(...v_vals) - Math.min(...v_vals);
    const still = hz_range < settleRad && v_range < settleRad;

    if (!still) {
      // Prism is moving to the next spot.
      armStartRef.current = null;
      if (mode !== "armed") setMode("armed");
      return;
    }
    // Still — start the settle timer if not already running.
    if (armStartRef.current == null) armStartRef.current = Date.now();
    if (Date.now() - armStartRef.current >= settleMs) fire();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.hz, scope.v, scope.atr_locked, mode, autoShoot, autoReacquire]);

  const doSearch = async () => {
    setBusy(true); setMode("searching");
    historyRef.current = []; armStartRef.current = null;
    lostSinceRef.current = null;
    try {
      await station.powerSearch();
      toast.success("Prism locked · move to your first point");
      // Land in DISARMED, not ARMED. Freshly-searched prism must be
      // physically moved before the arm-detector fires — otherwise
      // we'd immediately shoot at wherever the pole was resting
      // during the search, which isn't a real survey point.
      // Reference for re-arm = the Hz/V we're pointing at right now.
      lastShotRef.current = { hz: scope.hz, v: scope.v };
      setMode("disarmed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "No prism found");
      setMode("idle");
    } finally { setBusy(false); }
  };
  const doStop = async () => {
    setBusy(true);
    try {
      await station.atr("stop");
      toast("Search cancelled");
      setMode("idle");
    } catch (e) {
      toast.error(e?.message || "Stop failed");
    } finally {
      setBusy(false);
      reacquiringRef.current = false;
    }
  };
  const doUnlock = async () => {
    setBusy(true);
    try { await station.atr("unlock"); toast("Lock released"); setMode("idle"); }
    catch (e) { toast.error(e?.message || "Unlock failed"); }
    finally { setBusy(false); }
  };

  const modeLabel = useMemo(() => ({
    idle: "READY · aim near prism then SEARCH",
    searching: "SEARCHING…",
    armed: "LOCKED · hold prism still for auto-shot",
    shooting: "SHOOTING…",
    disarmed: "COOLDOWN · move the prism to re-arm",
    lost: "LOCK LOST · reacquiring…",
  }[mode] || mode), [mode]);
  const modeColor = ({
    idle: "#8B939A", searching: "#F5A623", armed: "#8DC63F",
    shooting: "#00E1C7", disarmed: "#8B939A", lost: "#FF3B30",
  }[mode] || "#8B939A");

  return (
    <div className="space-y-2">
      <div className="rounded-md border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest flex items-center justify-between"
           style={{ borderColor: modeColor, color: modeColor, background: `${modeColor}12` }}
           data-testid="shootpanel-prism-status">
        <span>{modeLabel}</span>
        {scope.atr_locked && <span className="rounded-sm bg-[#8DC63F]/20 px-1.5 py-0.5 text-[9px] text-[#8DC63F]">LOCK</span>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {mode === "searching" ? (
          <button data-testid="shootpanel-stop" onClick={doStop} disabled={busy && mode !== "searching"}
            className="min-h-[54px] rounded-md border border-[#FF3B30] bg-[#FF3B30]/15 py-2 text-sm font-black uppercase tracking-widest text-[#FF3B30] animate-pulse"
            style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            <PauseCircle size={16} className="mr-1 inline" strokeWidth={2.5} />
            Stop Search
          </button>
        ) : (
          <button data-testid="shootpanel-search" disabled={busy || disabled} onClick={doSearch}
            className="min-h-[54px] rounded-md border border-[#00E1C7] bg-gradient-to-br from-[#00E1C7]/20 to-[#005a52]/20 py-2 text-sm font-black uppercase tracking-widest text-[#00E1C7] disabled:opacity-40"
            style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            <ScanSearch size={16} className="mr-1 inline" strokeWidth={2.5} />
            Search
          </button>
        )}
        {scope.atr_locked ? (
          <button data-testid="shootpanel-unlock" disabled={busy || disabled || mode === "searching"} onClick={doUnlock}
            className="min-h-[54px] rounded-md border border-[#FF3B30]/60 bg-[#FF3B30]/10 py-2 text-sm font-black uppercase tracking-widest text-[#FF3B30] disabled:opacity-40"
            style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            <PauseCircle size={16} className="mr-1 inline" strokeWidth={2.5} />
            Unlock
          </button>
        ) : (
          <button data-testid="shootpanel-reacquire" disabled={busy || disabled || mode === "searching"}
            onClick={doReacquire}
            className="min-h-[54px] rounded-md border border-[#F5A623]/70 bg-[#F5A623]/10 py-2 text-sm font-black uppercase tracking-widest text-[#F5A623] disabled:opacity-40"
            style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            <ScanSearch size={16} className="mr-1 inline" strokeWidth={2.5} />
            Reacquire
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[#2A2E39] bg-[#14161A] px-2 py-1 text-[9px] uppercase tracking-widest text-[#8B939A]">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={autoShoot} onChange={(e) => setAutoShoot(e.target.checked)}
            data-testid="shootpanel-auto" className="h-3.5 w-3.5 accent-[#00E1C7]" />
          Auto-shoot on still
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={autoReacquire} onChange={(e) => setAutoReacquire(e.target.checked)}
            data-testid="shootpanel-auto-reacquire" className="h-3.5 w-3.5 accent-[#F5A623]" />
          Auto-reacquire
        </label>
        <button data-testid="shootpanel-manual-prism" disabled={busy || disabled || mode === "idle" || mode === "searching"}
          onClick={fire}
          className="rounded-sm border border-[#8DC63F]/60 px-2 py-0.5 text-[#8DC63F] hover:bg-[#8DC63F]/10 disabled:opacity-30">
          Shoot now
        </button>
      </div>
    </div>
  );
};

export default ShootPanel;
